import React, { useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useDropzone } from "react-dropzone";
import { Upload, X, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { uploadMultipleFiles, UploadProgress, updateWordDocumentFolder, fetchTemplateFolderById } from "../api/wordDocuments";
import "../styles/UploadModal.css";
import { useAuth } from "../context/AuthContext";

type FileWithPreview = {
  originalFile: File;
  preview?: string;
  progress?: number;
  error?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  currentFolderId?: string | null;
};

export function UploadModal({ isOpen, onClose, onUploadSuccess, currentFolderId }: Props) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { user } = useAuth();
  const tenantId = user?.tenantId;

  // Resetuj stan po zamknięciu modala
  React.useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setUploadError(null);
    }
  }, [isOpen]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      originalFile: file,
      preview: URL.createObjectURL(file),
      progress: 0
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true
  });

  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      const file = newFiles[index];
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleProgress = useCallback((progress: UploadProgress) => {
    setFiles(prev => prev.map(file => 
      file.originalFile.name === progress.fileName
        ? { ...file, progress: progress.progress, error: progress.error }
        : file
    ));
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      await uploadMultipleFiles(tenantId, files.map(f => f.originalFile), handleProgress, currentFolderId);
      if (currentFolderId && tenantId) {
        const folderData = await fetchTemplateFolderById(tenantId, currentFolderId);
        for (const file of files) {
          const uploaded = folderData.documents && folderData.documents.find((d: any) => d.fileName === file.originalFile.name);
          if (uploaded) {
            await updateWordDocumentFolder(tenantId, uploaded.id, currentFolderId);
          }
        }
      }
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError("Wystąpił błąd podczas przesyłania plików. Spróbuj ponownie.");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginLeft: 20 }}>Dodaj szablon</h2>          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div 
          {...getRootProps()} 
          className={`dropzone ${isDragActive ? 'active' : ''} ${files.length > 0 ? 'has-files' : ''}`}
        >
          <input {...getInputProps()} />
          <Upload size={40} className="upload-icon" />
          <p className="dropzone-text">
            {isDragActive
              ? "Upuść pliki tutaj..."
              : "Przeciągnij i upuść pliki Word tutaj lub kliknij, aby wybrać"}
          </p>
          <p className="dropzone-hint">
            Obsługiwane formaty: .docx, .doc (max 10MB)
          </p>
        </div>

        {files.length > 0 && (
          <div className="files-list">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-info">
                  <FileText size={20} />
                  <span className="file-name">{file.originalFile.name}</span>
                  <span className="file-size">
                    ({(file.originalFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <div className="file-status">
                  {file.error ? (
                    <AlertCircle size={20} className="error-icon" />
                  ) : file.progress === 100 ? (
                    <CheckCircle2 size={20} className="success-icon" />
                  ) : (
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${file.progress || 0}%` }}
                      />
                    </div>
                  )}
                  <button 
                    className="remove-file" 
                    onClick={() => removeFile(index)}
                    disabled={isUploading}
                  >
                    <X size={16} />
        </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {uploadError && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{uploadError}</span>
          </div>
        )}

        <div className="modal-footer">
          <Button 
            className="outline-button" 
            onClick={onClose}
            disabled={isUploading}
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={files.length === 0 || isUploading}
            className="upload-button"
          >
            {isUploading ? "Przesyłanie..." : "Prześlij pliki"}
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
